import React, { useState } from "react";
import { Box, Button, Flex, Image, Text } from "@chakra-ui/react";
import { FiTrash2 } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import moment from "moment";
import { Loading } from "../../components/ui";
import { AppBar } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopButton,
  DesktopPage,
  DesktopPanel,
  DetailItem,
  DetailSection,
  useIsDesktop,
} from "../../components/desktopUi";
import ConfirmDialog from "../../components/ConfirmDialog";
import { PropCell, StatusPill, BORDER, MUTED, TEXT } from "../../components/requestUi";
import { deleteMakeupWorkApi, getMakeupWorkByIdApi } from "../../api/requests.api";
import { extractErrorMessage } from "../../api/client";
import { resolveImageUrl } from "../../utils/format";
import { EMP_API_BASE_URL, EMP_BASE_PATH } from "../../config";

const MakeupDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["emp", "makeup-detail", id],
    queryFn: () => getMakeupWorkByIdApi(id),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteMakeupWorkApi(id),
    onSuccess: () => {
      toast.success("Đã xóa đăng ký làm bù");
      queryClient.invalidateQueries({ queryKey: ["emp", "makeup-list"] });
      navigate(`${EMP_BASE_PATH}/lam-bu`);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Xóa đơn thất bại")),
  });

  const images = data?.images || [];
  const canDelete = String(data?.status || "").toUpperCase() === "PENDING";

  const confirmDialog = (
    <ConfirmDialog
      isOpen={confirmDelete}
      title="Xóa đăng ký làm bù"
      description="Bạn chắc chắn muốn xóa đăng ký làm bù này?"
      confirmText="Xóa"
      cancelText="Hủy"
      danger
      loading={deleteMutation.isPending}
      onConfirm={() => {
        setConfirmDelete(false);
        deleteMutation.mutate();
      }}
      onClose={() => setConfirmDelete(false)}
    />
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage
        title="Chi tiết đơn làm bù"
        right={<DesktopBack onClick={() => navigate(`${EMP_BASE_PATH}/lam-bu`)} />}
      >
        {isLoading ? (
          <Loading />
        ) : isError || !data ? (
          <Text fontSize="14px" color={MUTED}>
            Không tải được dữ liệu.
          </Text>
        ) : (
          <>
            <DetailSection
              title={`Đơn ngày ${
                data.createdAt ? moment(data.createdAt).format("DD/MM/YYYY") : "--/--/----"
              }`}
              right={
                <Flex align="center" gap="12px">
                  <StatusPill status={data.status} />
                  {canDelete && (
                    <DesktopButton
                      variant="danger"
                      icon={<Box as={FiTrash2} fontSize="16px" />}
                      isLoading={deleteMutation.isPending}
                      onClick={() => setConfirmDelete(true)}
                    >
                      Xóa đơn
                    </DesktopButton>
                  )}
                </Flex>
              }
            >
              <DetailItem
                label="Ngày làm bù"
                value={data.dayLeave ? moment(data.dayLeave).format("DD/MM/YYYY") : "-"}
              />
              <DetailItem label="Số công" value={data.workingDayRate ?? 0} />
              <DetailItem label="Nội dung" value={data.note} />
            </DetailSection>

            {images.length > 0 && (
              <DesktopPanel>
                <Text fontSize="16px" fontWeight={600} mb="16px" color={TEXT}>
                  Ảnh minh chứng
                </Text>
                <Flex gap="16px" flexWrap="wrap">
                  {images.map((src, i) => (
                    <Image
                      key={i}
                      src={resolveImageUrl(src, EMP_API_BASE_URL)}
                      alt=""
                      w="280px"
                      h="200px"
                      objectFit="cover"
                      borderRadius="12px"
                      border="1px solid"
                      borderColor={BORDER}
                    />
                  ))}
                </Flex>
              </DesktopPanel>
            )}
          </>
        )}

        {confirmDialog}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto">
      <AppBar title="Chi tiết đơn làm bù" onBack={() => navigate(`${EMP_BASE_PATH}/lam-bu`)} />

      <Box px="16px" pt="16px">
      {isLoading ? (
        <Loading />
      ) : isError || !data ? (
        <Text fontSize="14px" color={MUTED}>Không tải được dữ liệu.</Text>
      ) : (
        <Box bg="white" borderRadius="12px" border="1px solid" borderColor={BORDER} px="16px" pt="16px" pb="16px">
          <Flex align="center" gap="8px" flexWrap="wrap">
            <Text fontSize="14px" fontWeight={600} color={TEXT}>
              {data.createdAt ? moment(data.createdAt).format("DD/MM/YYYY") : "--/--/----"}
            </Text>
            <Text fontSize="14px" color={TEXT}>-</Text>
            <StatusPill status={data.status} />
          </Flex>

          <PropCell label="Ngày làm bù" value={data.dayLeave ? moment(data.dayLeave).format("DD/MM/YYYY") : "-"} />
          <PropCell label="Số công" value={data.workingDayRate ?? 0} />
          <PropCell label="Nội dung" value={data.note} borderBottom={images.length > 0} />

          {images.length > 0 && (
            <Box pt="16px">
              <Text fontSize="14px" fontWeight={600} mb="12px" color={TEXT}>Ảnh minh chứng</Text>
              <Flex direction="column" gap="16px">
                {images.map((src, i) => (
                  <Image
                    key={i}
                    src={resolveImageUrl(src, EMP_API_BASE_URL)}
                    alt=""
                    w="100%"
                    h="240px"
                    objectFit="cover"
                    borderRadius="12px"
                    border="1px solid"
                    borderColor={BORDER}
                  />
                ))}
              </Flex>
            </Box>
          )}

          {canDelete && (
            <Button
              mt="18px"
              w="100%"
              leftIcon={<FiTrash2 />}
              variant="outline"
              borderColor="#E5484D"
              color="#E5484D"
              _hover={{ bg: "#FFF0F0" }}
              isLoading={deleteMutation.isPending}
              onClick={() => setConfirmDelete(true)}
            >
              Xóa đơn
            </Button>
          )}
        </Box>
      )}
      </Box>

      {confirmDialog}
    </Box>
  );
};

export default MakeupDetail;
